import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import StreamDisplay from 'features/streaming/components/stream-display/StreamDisplay';
import RemoteAudioWrapper from 'features/streaming/components/media/remote-stream/RemoteAudioWrapper';
import { StreamBadge } from 'features/streaming/components/stream-display/StreamBadge';
import { UserScreenshareSource } from 'features/layout/features/order/types';
import { screenshareSelected } from 'features/screenshare/actions';
import { useAppDispatch } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import { isTouchDevice } from 'utils/isTouchDevice';

type ScreenshareTileProps = {
  screenshare: UserScreenshareSource;
};

export const ScreenshareTile = ({ screenshare }: ScreenshareTileProps) => {
  const { t } = useTranslation(['room', 'common']);

  const dispatch = useAppDispatch();

  const handleClick = () => {
    if (screenshare?.feedId) {
      dispatch(
        screenshareSelected({
          feedId: screenshare.feedId,
          userId: screenshare.userId,
          update: true,
        })
      );
    }
  };

  const label = useMemo(() => {
    if (screenshare.kind === 'screenshare-local') {
      return t('you');
    }

    return screenshare.user?.name;
  }, [screenshare.kind, screenshare.user?.name, t]);

  return (
    <Box
      onClick={handleClick}
      sx={(theme) => ({
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid white',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.1s ease',
        ...(isTouchDevice()
          ? {}
          : {
              '&:hover': {
                borderColor: theme.palette.primary.main,
              },
            }),
      })}
    >
      <StreamDisplay source={screenshare} />
      {screenshare.kind !== 'screenshare-local' && <RemoteAudioWrapper source={screenshare} />}
      <StreamBadge label={`${label} (${t('screenshare.single')})`} />
    </Box>
  );
};
